import { Component, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import {
  FROMUPDATECUSTOMER,
  ModalDialogType,
  OnBaseCatalogueType
} from 'src/app/@core/constants';
import { PosConfirmationService } from 'src/app/@core/services/pos-confirmation.service';
import { Customer } from 'src/app/models/customer';
import { Param } from 'src/app/models/param';
import { CustomerCreditStatusRequest } from 'src/app/models/request/customer-credit-status-request';
import { ReasonLogRequest } from 'src/app/models/request/reason-log-request-model';
import { CreditPolicyResponseModel } from 'src/app/models/response/credit-policy-response';
import { CreditStatusFranchisedResponseModel } from 'src/app/models/response/customerCreditStatusResponseModel';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { CustomerService } from 'src/app/services/customer.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { CustomerCreditRequestStepperComponent } from '..';
import { PosReasonSelectModalService } from './../../../../@core/services/pos-reason-select-modal.service';
import { PosVerifyCustomerModalService } from './../../../../@core/services/pos-verify-customer-modal.service';
import { CustomerCreditStatusResponse } from './../../../../models/response/customer-credit-status-response';
import { CustomerTableModel } from './../../../../models/table-models/customer-table';
import { User } from './../../../../models/user';
import { AuthService } from './../../../../services/auth.service';
import { CreditPolicyService } from './../../../../services/credit-policy.service';
import { CustomerVerificationService } from './../../../../services/customer-verification.service';
import { OnbaseService } from './../../../../services/onbase.service';

@Component({
  selector: 'app-customer-identification',
  templateUrl: './customer-identification.component.html',
  styleUrls: ['./customer-identification.component.sass'],
})
export class CustomerIdentificationComponent implements OnInit {
  @ViewChild('creditRequestStepper')
  creditRequestStepper!: CustomerCreditRequestStepperComponent;

  currentCustomer: Customer;
  currentCustomerCreditConditions: CreditStatusFranchisedResponseModel;
  currentCustomerCreditStatus: CustomerCreditStatusResponse | undefined;
  currentCustomerCreditPolicy: CreditPolicyResponseModel | undefined;
  homeTypes: Array<Param>;
  preApprovalForm: FormGroup;
  productSelectionForm: FormGroup;
  selectedSeller: CustomerTableModel | User | undefined;

  creditRequestsAmount: number = 0;
  creditNotesAmount: number = 0;
  loansAmount: number = 0;
  canceledLoansAmount: number = 0;
  ordersAmount: number = 0;

  comeFromQuotationPlanClick: boolean = false;

  amountOfRecollections: number = 0;

  constructor(
    private onBaseService: OnbaseService,
    private customerService: CustomerService,
    private configService: ConfigurationService,
    private authService: AuthService,
    private creditPolicyService: CreditPolicyService,
    private fb: FormBuilder,
    private posConfirmationService: PosConfirmationService,
    public router: Router,
    private customerVerificationService: CustomerVerificationService,
    private verifyCustomerModalService: PosVerifyCustomerModalService,
    private reasonSelectModalService: PosReasonSelectModalService,
    private localStorageService: LocalStorageService
  ) {
    this.currentCustomer = new Customer();
    this.currentCustomerCreditConditions =
      new CreditStatusFranchisedResponseModel();
    this.homeTypes = new Array();
    this.currentCustomerCreditStatus = undefined;

    this.preApprovalForm = this.fb.group({
      workSeniority: new FormControl(undefined, [Validators.required]),
      homeType: new FormControl(undefined, [Validators.required]),
      ownVehicle: new FormControl(false, [Validators.required]),
      monthIncome: new FormControl(),
    });

    this.productSelectionForm = this.fb.group({
      productCode: this.fb.control(undefined, [Validators.required]),
    });

    if (this.creditRequestStepper) {
      this.preApprovalForm.valueChanges.subscribe(
        (formValue) => (this.creditRequestStepper.alreadyPreApproved = false)
      );
    }
  }

  private setInitialState() {
    this.currentCustomer = new Customer();
    this.currentCustomerCreditConditions =
      new CreditStatusFranchisedResponseModel();
    this.homeTypes = new Array();
    this.currentCustomerCreditStatus = undefined;
  }

  ngOnInit(): void { }

  async handleSelectedCustomerChange(customerData: {
    customer: Customer;
    customerCreditConditions: CreditStatusFranchisedResponseModel;
    selectedSeller: CustomerTableModel | User | undefined;
  }): Promise<void> {
    this.currentCustomer = customerData.customer;
    this.currentCustomerCreditConditions =
      customerData.customerCreditConditions;
    this.selectedSeller = customerData.selectedSeller;

    if (this.currentCustomerCreditConditions.creditLineActive) {
      const { fiscalId, fiscalIdType } = this.currentCustomer.mainPersonalInfo!;
      const customerCreditStatus = await this.customerService
        .readCustomerCreditStatus(
          new CustomerCreditStatusRequest(
            fiscalId,
            fiscalIdType,
            this.configService.readSelectedBranchAndSalesOrg().branch.code,
            this.isCustomer(customerData.selectedSeller)
              ? customerData.selectedSeller.code
              : customerData.selectedSeller!.username,
            'CUSTOMERS'
          )
        )
        .toPromise();

      this.currentCustomerCreditStatus = customerCreditStatus;

      const customerCreditPolicyResponse = await this.creditPolicyService
        .getCreditPolicy({
          riskLevel: customerCreditStatus.claseRiesgo!,
          age: `${this.currentCustomer.mainPersonalInfo!.age}`,
          creditGroup: customerCreditStatus.grupoCreditoCliente!,
          creditProcedure: customerCreditStatus.procedimientoCredito!,
          personality: this.currentCustomer.mainPersonalInfo!.personality,
          infoCategory: this.currentCustomerCreditStatus.informacionGarantia
            ? this.currentCustomerCreditStatus.informacionGarantia
              ?.claseGarantia
            : undefined,
          salesOrg:
            this.configService.readSelectedBranchAndSalesOrg().salesOrg.code,
        })
        .toPromise();

      this.currentCustomerCreditPolicy =
        customerCreditPolicyResponse.response.content[0];
    }

    await this.fetchHomeTypes();
    this.backFormToInitialState();

    if (this.creditRequestStepper) { this.creditRequestStepper.resetStepper(); }

    const customerNeedUpdateResponse = (await this.customerService
      .customerNeedUpdate(this.currentCustomer.code, this.configService.readSelectedBranchAndSalesOrg().branch.code)
      .toPromise())
      .response;

    this.localStorageService.set('customerVerifyData', JSON.stringify(customerNeedUpdateResponse.verifyData));

    if (customerNeedUpdateResponse.customerNeedUpdate) {

      const userWantsToUpdateCustomer = await this.posConfirmationService.showAsync(
        'Actualización de datos',
        `Este cliente necesita actualización de datos ¿Desea ir a actualización de clientes?, ${customerNeedUpdateResponse.message.replace(/\bnull\b/gi, '')}`,
        ModalDialogType.DANGER,
      );

      if (userWantsToUpdateCustomer) {
        this.router.navigate(
          ['/customer/update', this.currentCustomer.code],
          {
            state: {
              from: FROMUPDATECUSTOMER
            }
          }
        );
      } else {
        const selectedReason = await this.reasonSelectModalService.show('verifyCustomer');

        const requestToSaveReasonLog: ReasonLogRequest = {
          branchCode: this.configService.readSelectedBranchAndSalesOrg().branch.code,
          component: 'CUSTOMERS',
          customerCode: this.currentCustomer.code,
          description: selectedReason?.description || "",
          reasonCode: selectedReason?.itemCode || "RA",
          sellerCode: (await this.authService.readProfile()).username,
          salesOrgCode: this.configService.readSelectedBranchAndSalesOrg().salesOrg.code,
          yesOrNo: 'NO',
          phoneNumber: selectedReason?.ownerPhone ?? ""
        };
        await this.customerVerificationService.saveReasonToNotUpdateCustomer(requestToSaveReasonLog).toPromise();
      }
    }

    // else {
    //   const customerVerifyCheckData = (
    //     await this.customerVerificationService
    //       .getCustomerContactInformationVerification(this.currentCustomer.code)
    //       .toPromise()
    //   ).response.data;
    //   if (
    //     !customerVerifyCheckData.email.verified ||
    //     !customerVerifyCheckData.phone.verified
    //   ) {
    //     const userWantsToVerifyResponse =
    //       await this.posConfirmationService.showAsync(
    //         `Verificacion de datos`,
    //         `Este cliente tiene datos que aun no han sido verificados. ¿Desea verificar los datos?`,
    //         ModalDialogType.DANGER
    //       );

    //     if (userWantsToVerifyResponse) {
    //       const responseVerificationProcess =
    //         await this.verifyCustomerModalService.show(
    //           customerVerifyCheckData,
    //           this.currentCustomer.code
    //         );
    //       if (responseVerificationProcess) {
    //         Swal.fire({
    //           title: 'Verificacion de datos',
    //           text: 'Se completo el proceso de verificacion exitosamente.',
    //         });
    //       }
    //     } else {
    //       alert('deberias de verificar picaron');
    //     }
    //   }
    // }
  }

  private backFormToInitialState(): void {
    this.preApprovalForm.patchValue({
      amountToBill: 0,
      monthIncome: 0,
      workSeniority: undefined,
      homeType: undefined,
      ownVehicle: false,
    });
  }

  async fetchHomeTypes(): Promise<void> {
    this.homeTypes = await this.onBaseService
      .readOnBaseCatalogueObject(
        OnBaseCatalogueType.HOME,
        this.currentCustomer.mainPersonalInfo?.personality
      )
      .toPromise();
  }

  validateCustomerStatus() {
    return this.currentCustomerCreditConditions.estadoCliente != 'BLQ';
  }

  handleIsCustomerChanging(isCustomerChanging: boolean) {
    if (isCustomerChanging) {
      this.setInitialState();
    }
  }

  isCustomer(
    value: CustomerTableModel | User | undefined
  ): value is CustomerTableModel {
    return (value as CustomerTableModel).fullName !== undefined;
  }

  getAmountofCreditRequests(amount: number) {
    this.creditRequestsAmount = amount;
  }
  getAmountofCreditNotes(amount: number) {
    this.creditNotesAmount = amount;
  }
  getAmountofLoans(amount: number) {
    this.loansAmount = amount;
  }
  getAmountofOrders(amount: number) {
    this.ordersAmount = amount;
  }

  handleQuoationPlanClick(isClick: boolean) {
    this.comeFromQuotationPlanClick = isClick;
  }

  getAmountofLoansCanceled(amount: number) {
    this.canceledLoansAmount = amount;
  }

  handleAmountOfRecollectionChange(amount: number) {
    this.amountOfRecollections = amount;
  }
}
